import { useCallback } from 'react';
import Swal from 'sweetalert2';
import { const_routes } from './const_routes';
import { fetchToken } from './fetch';
import { RolesEnum } from '../types/RolesEnum';
import { normalizeSpecialistRoleKey } from './activeSpecialistRoleHelper';
import { mergePreservingMultilevelSelects } from './helperConcat';

const MODULE_FLOW_EDIT = {
  technology: { route: const_routes.ROUTE_TECHNOLOGY, key: 'technology' },
  alternative: { route: const_routes.ROUTE_ALTERNATIVE, key: 'alternative' },
  experience: { route: const_routes.ROUTE_EXPERIENCE, key: 'experience' },
  innovation: { route: const_routes.ROUTE_INNOVATION, key: 'innovation' },
  assistance: { route: const_routes.ROUTE_ASSISTANCE, key: 'assistance' },
  financial: { route: const_routes.ROUTE_FINANCIAL, key: 'financial' },
  vision: { route: const_routes.ROUTE_VISION, key: 'vision' },
  regulation: { route: const_routes.ROUTE_REGULATION, key: 'regulation' }
};

const PRE_MANAGER_CATALOG_ROLE_ID = 4;

/** En TECCC el login guarda `user_id`, no `id`. `auth.user.id` queda undefined. */
export const resolveAuthUserId = (authUser) => authUser?.user_id || authUser?.id || null;

export const isRecordInCurrentBandeja = (record, roleId) => {
  if (!record?.current_role_catalog_id || !roleId) {
    return true;
  }
  return Number(record.current_role_catalog_id) === Number(roleId);
};

/** Caso en Autoasignación: rol de bandeja puesto, sin usuario, no publicado. */
export const isFlowRecordInAssignmentPool = (record) => {
  if (!record || record.published_at || !record.current_role_catalog_id) {
    return false;
  }
  const userId = record.current_user_id;
  if (userId == null || userId === '') {
    return true;
  }
  const numeric = Number(userId);
  if (Number.isFinite(numeric)) {
    return numeric <= 0;
  }
  return false;
};

/** Solo el pool que sigue en rol Pre-Gestor (CMI / legado). Gestor/V/E tienen su propio pool. */
export const isFlowRecordInPrePool = (record) => (
  isFlowRecordInAssignmentPool(record)
  && Number(record.current_role_catalog_id) === PRE_MANAGER_CATALOG_ROLE_ID
);

export const resolveManagerCatalogRoleId = (catalogRoles = []) => {
  const row = (catalogRoles || []).find((role) => role.name === RolesEnum.SPECIALIST_MANAGER);
  return row?.id ? Number(row.id) : null;
};

export const selectionIsPrePool = (record, selectedRecords = []) => {
  const targets = (selectedRecords || []).filter((item) => item && typeof item === 'object');
  const rows = targets.length ? targets : (record ? [record] : []);
  return rows.length > 0 && rows.every(isFlowRecordInPrePool);
};

const resolveSurfaceRoleKey = (catalogRoles = [], trayRoleId = null, roleContext = {}) => {
  const trayRole = (catalogRoles || []).find((role) => Number(role.id) === Number(trayRoleId));
  if (trayRole) {
    return normalizeSpecialistRoleKey(trayRole);
  }
  return normalizeSpecialistRoleKey({
    name: roleContext.roleName,
    code: roleContext.roleCode,
    roleKey: roleContext.roleCode
  });
};

/**
 * Remap a reglas de Gestor solo si el caso sigue en rol Pre (pool legado / CMI).
 * Autoasignación de Gestor/V/E usa el rol de esa bandeja.
 */
export const canClaimFromPrePoolSurface = (surfaceKey, roleContext = {}, trayRoleId = null) => {
  const key = String(surfaceKey || '').toLowerCase();
  if (['pre-manager', 'feeder', 'validator', 'editor'].includes(key)) {
    return false;
  }
  if (key === 'manager' || key === 'coordinator') {
    return true;
  }
  return Boolean(roleContext.isAdmin && !trayRoleId);
};

const isCoordinatorRoleContext = (roleContext = {}, surfaceKey = '') => {
  const key = String(roleContext.roleCode || surfaceKey || '').toLowerCase();
  return key === 'coordinator' || key.includes('coordinador');
};

const isLiaisonRoleContext = (roleContext = {}, surfaceKey = '') => {
  const key = String(roleContext.roleCode || surfaceKey || '').toLowerCase();
  return key.includes('liaison') || key.includes('enlace');
};

const resolveSelectionTrayRoleId = (record, selectedRecords = []) => {
  const first = record || selectedRecords[0] || null;
  return Number(first?.current_role_catalog_id || 0);
};

const selectionIsPublished = (record, selectedRecords = []) => {
  const rows = (selectedRecords || []).filter((item) => item && typeof item === 'object');
  const targets = rows.length ? rows : (record ? [record] : []);
  return targets.length > 0 && targets.every((row) => row.published_at);
};

/**
 * Rol con el que el panel carga ruleflows y opera.
 * Un DRI en pool Pre (rol 4, user null) lo reclama el Gestor.
 * Los pools de Gestor/V/E ya traen current_role de esa bandeja.
 * El Coordinador, en no publicados, opera con las reglas de esa bandeja
 * (no con las suyas de publicación). En publicados usa su propio rol.
 */
export const resolveOperatingFlowRoleId = ({
  record,
  selectedRecords = [],
  catalogRoles = [],
  trayRoleId = null,
  roleContext = {}
} = {}) => {
  const surfaceKey = resolveSurfaceRoleKey(catalogRoles, trayRoleId, roleContext);
  const coordinator = isCoordinatorRoleContext(roleContext, surfaceKey);
  const liaison = isLiaisonRoleContext(roleContext, surfaceKey);
  const recordPublished = selectionIsPublished(record, selectedRecords);
  const recordTrayRoleId = resolveSelectionTrayRoleId(record, selectedRecords);

  if (coordinator && !recordPublished && recordTrayRoleId) {
    if (
      selectionIsPrePool(record, selectedRecords)
      && canClaimFromPrePoolSurface('coordinator', roleContext, recordTrayRoleId)
    ) {
      const managerId = resolveManagerCatalogRoleId(catalogRoles);
      if (managerId) {
        return managerId;
      }
    }
    return recordTrayRoleId;
  }

  if ((coordinator || liaison) && recordPublished && roleContext.roleId) {
    return Number(roleContext.roleId);
  }

  if (
    selectionIsPrePool(record, selectedRecords)
    && canClaimFromPrePoolSurface(surfaceKey, roleContext, trayRoleId)
  ) {
    const managerId = resolveManagerCatalogRoleId(catalogRoles);
    if (managerId) {
      return managerId;
    }
  }
  if (trayRoleId) {
    return Number(trayRoleId);
  }
  if ((coordinator || liaison) && roleContext.roleId) {
    return Number(roleContext.roleId);
  }
  if (roleContext.isAdmin) {
    return Number(
      record?.current_role_catalog_id
      || selectedRecords[0]?.current_role_catalog_id
      || 0
    ) || roleContext.roleId;
  }
  return roleContext.roleId;
};

export const isFlowRecordPublished = (record, flowEngine = {}) => {
  if (!record) {
    return false;
  }
  if (record.published_at) {
    return true;
  }
  const state = (flowEngine.states || []).find(
    (item) => Number(item.id) === Number(record.state_catalog_id)
  );
  const name = String(state?.name || '').trim().toLowerCase();
  return name === 'publicado' || name === 'published' || name === 'aprobado';
};

export const canOperateFlowRecord = ({
  record,
  roleId,
  flowEngineEnabled,
  flowEngine = {},
  canOperate = true,
  roleKey = '',
  selectedRoleKey = '',
  actorUserId = null,
  allowedProcesses = null,
  process = ''
}) => {
  if (!canOperate) {
    return false;
  }
  const processKey = String(process || record?.process || '').toLowerCase();
  if (Array.isArray(allowedProcesses) && processKey && !allowedProcesses.includes(processKey)) {
    return false;
  }
  if (!flowEngineEnabled) {
    return true;
  }
  const activeKey = String(selectedRoleKey || roleKey || '').toLowerCase();
  if (isFlowRecordPublished(record, flowEngine)) {
    return ['coordinator', 'liaison'].includes(activeKey);
  }
  if (isFlowRecordInAssignmentPool(record)) {
    if (['coordinator', 'liaison'].includes(activeKey)) {
      return true;
    }
    return Number(record.current_role_catalog_id) === Number(roleId);
  }
  if (!record?.current_role_catalog_id) {
    return !record?.last_transition_at;
  }
  if (!isRecordInCurrentBandeja(record, roleId) && !['coordinator', 'liaison'].includes(activeKey)) {
    return false;
  }
  if (record?.current_user_id && actorUserId && !['coordinator', 'liaison'].includes(activeKey)) {
    return Number(record.current_user_id) === Number(actorUserId);
  }
  return true;
};

const isSuperOperateRoleKey = (roleKey = '') => {
  const key = String(roleKey || '').trim().toLowerCase();
  return key === 'coordinator'
    || key === 'liaison'
    || key.includes('coordinador')
    || key.includes('enlace');
};

export const canSaveFlowRecord = ({
  record,
  roleId,
  flowEngineEnabled,
  flowEngine = {},
  roleKey = '',
  actorUserId = null,
  trayView = ''
}) => {
  if (!flowEngineEnabled) {
    return true;
  }
  if (isFlowRecordPublished(record, flowEngine)) {
    return isSuperOperateRoleKey(roleKey);
  }
  const view = String(trayView || '').trim().toLowerCase();
  if (view === 'general' && !isSuperOperateRoleKey(roleKey)) {
    return false;
  }
  if (!isRecordInCurrentBandeja(record, roleId) && !isSuperOperateRoleKey(roleKey)) {
    return false;
  }
  if (isFlowRecordInAssignmentPool(record)) {
    return isSuperOperateRoleKey(roleKey);
  }
  if (isSuperOperateRoleKey(roleKey)) {
    return true;
  }
  if (!actorUserId || record?.current_user_id == null || record?.current_user_id === '') {
    return false;
  }
  return Number(record.current_user_id) === Number(actorUserId);
};

export const canSoftDeleteFlowRecord = ({
  record,
  flowEngineEnabled,
  canDelete,
  selectedRole,
  flowEngine = {}
}) => {
  if (!canDelete || !record) {
    return false;
  }
  if (!flowEngineEnabled) {
    return true;
  }
  if (isFlowRecordPublished(record, flowEngine)) {
    return false;
  }
  if (selectedRole?.id && record.current_role_catalog_id) {
    return Number(record.current_role_catalog_id) === Number(selectedRole.id);
  }
  return !record.current_role_catalog_id;
};

export const reloadModuleRecord = async (moduleProcess, recordId) => {
  const config = MODULE_FLOW_EDIT[moduleProcess];
  if (!config || !recordId) {
    return null;
  }

  const resp = await fetchToken(
    `${const_routes.SCHEMA}`,
    `${const_routes.VERSIONV1}`,
    config.route,
    `get/${recordId}`
  );
  const body = await resp.json();

  if (body.ok && body[config.key]) {
    return body[config.key];
  }
  return null;
};

export const useFlowEditAfterAction = ({
  moduleProcess,
  recordId,
  dispatch,
  setValues,
  selectAction
}) => {
  return useCallback(async () => {
    if (!recordId) {
      return;
    }

    try {
      const record = await reloadModuleRecord(moduleProcess, recordId);
      if (record) {
        dispatch(selectAction(record));
        setValues((prev) => mergePreservingMultilevelSelects(prev, record));
      }
    } catch {
      // El panel conservará el estado previo si la recarga falla.
    }
  }, [moduleProcess, recordId, dispatch, setValues, selectAction]);
};

export const buildModuleFieldLabel = (moduleProcess, field, t) => {
  if (field === 'catalogproblem_list') {
    return t(`${moduleProcess}.short.catalogproblem_list`, 'Problemas socio climáticos');
  }
  return t(`${moduleProcess}.short.${field}`, field);
};

export const buildCatalogLookups = ({
  catalog = {},
  catalogCiiu = {},
  catalogSection = {},
  catalogPhase = {},
  locationCountry = {}
} = {}) => {
  const lookups = {
    catalogserie_id: catalog.listSeries || [],
    catalogsophistication_id: catalog.listSophistications || [],
    catalogmagnitude_id: catalog.listMagnitudes || [],
    catalogsize_id: catalog.listSizes || [],
    catalogtypology_id: catalog.listTypologies || [],
    catalogpricerange_id: catalog.listPriceRanges || [],
    catalogpermissionpublish_id: catalog.listPermissionPublish || [],
    cataloggeneraladaptation_id: catalog.listGeneralAdaptations || [],
    catalogstructuraladaptation_id: catalog.listStructuralAdaptations || [],
    catalogspecificadaptation_id: catalog.listSpecificAdaptations || [],
    catalogcomplexity_id: catalog.listComplexities || [],
    country_id: locationCountry.listCountries || [],
    region_id: locationCountry.listRegions || [],
    continent_id: locationCountry.listContinents || [],
    catalogciius_id: catalogCiiu.listFullCatalogCiius || [],
    catalogsections_id: catalogSection.listFullCatalogSections || [],
    catalogphases_id: catalogPhase.listFullCatalogPhases || []
  };

  for (let level = 1; level <= 6; level += 1) {
    lookups[`catalogciiu_level${level}`] = catalogCiiu.listFullCatalogCiius || [];
    lookups[`catalogsection_level${level}`] = catalogSection.listFullCatalogSections || [];
    lookups[`catalogphase_level${level}`] = catalogPhase.listFullCatalogPhases || [];
  }

  return lookups;
};

export const buildUsersByIdFromEvents = (events = [], authUser = null) => {
  const map = {};

  (events || []).forEach((event) => {
    if (event.user_id && event.user) {
      map[event.user_id] = event.user;
    }
    const roleFlowUser = event.roleFlow?.user;
    if (event.roleFlow?.user_id && roleFlowUser) {
      map[event.roleFlow.user_id] = roleFlowUser;
    }
  });

  const authId = resolveAuthUserId(authUser);
  if (authId && authUser) {
    map[authId] = authUser;
  }

  return map;
};

export const attachCoordinatorPublishedEditNote = async (valuesSubmit, {
  record,
  roleKey,
  flowEngine = {},
  t
} = {}) => {
  if (!isFlowRecordPublished(record, flowEngine)) {
    return { proceed: true, valuesSubmit };
  }
  if (!['coordinator', 'liaison'].includes(String(roleKey || '').toLowerCase())) {
    return { proceed: false, valuesSubmit };
  }

  const result = await Swal.fire({
    title: t('general.flow-engine.published-edit-title', 'Comentario de modificación'),
    text: t(
      'general.flow-engine.published-edit-text',
      'Indique el motivo de la modificación del registro publicado. Quedará en el historial.'
    ),
    input: 'textarea',
    inputPlaceholder: t('general.flow-engine.published-edit-placeholder', 'Comentario obligatorio'),
    showCancelButton: true,
    confirmButtonText: t('buttons.save', 'Guardar'),
    cancelButtonText: t('buttons.cancel', 'Cancelar'),
    inputValidator: (value) => {
      if (!String(value || '').trim()) {
        return t('general.flow-engine.published-edit-required', 'El comentario es obligatorio.');
      }
      return undefined;
    }
  });

  if (!result.isConfirmed) {
    return { proceed: false, valuesSubmit };
  }

  return {
    proceed: true,
    valuesSubmit: {
      ...valuesSubmit,
      flow_edit_note: String(result.value || '').trim()
    }
  };
};
